import { officeUrlsMatch, toOfficeUrl } from "@/src/utils/accessControl/formatOfficeUrl";
import { normalizeFormOfficeForApi, type FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";

const readNestedString = (row: Record<string, unknown>, path: string[]): string => {
  let current: unknown = row;
  for (const key of path) {
    if (current == null || typeof current !== "object") return "";
    current = (current as Record<string, unknown>)[key];
  }
  return current == null ? "" : String(current).trim();
};

/** Read `form_office` from root or nested applicant/user info shapes. */
export const readRowFormOffice = (row: unknown): string => {
  if (row == null || typeof row !== "object") return "";
  const record = row as Record<string, unknown>;

  const candidates = [
    readNestedString(record, ["form_office"]),
    readNestedString(record, ["formOffice"]),
    readNestedString(record, ["office"]),
    readNestedString(record, ["campus"]),
    readNestedString(record, ["applicant_info", "form_office"]),
    readNestedString(record, ["applicantInfo", "form_office"]),
    readNestedString(record, ["user_info", "form_office"]),
    readNestedString(record, ["userInfo", "form_office"]),
  ];

  return candidates.find((value) => value.trim()) ?? "";
};

/** Read `office_url` from root or nested applicant/user info shapes. */
export const readRowOfficeUrl = (row: unknown): string => {
  if (row == null || typeof row !== "object") return "";
  const record = row as Record<string, unknown>;

  const candidates = [
    readNestedString(record, ["office_url"]),
    readNestedString(record, ["officeUrl"]),
    readNestedString(record, ["applicant_info", "office_url"]),
    readNestedString(record, ["applicantInfo", "office_url"]),
    readNestedString(record, ["user_info", "office_url"]),
    readNestedString(record, ["userInfo", "office_url"]),
  ];

  return candidates.find((value) => value.trim()) ?? "";
};

/**
 * Site / `office_url` query.
 * Not sent → keep the row. Sent → row must have the same domain (missing URL fails).
 */
export const matchesSiteAccess = (officeUrl: string | null | undefined, selectedSiteUrl: string): boolean => {
  const selected = String(selectedSiteUrl ?? "").trim();
  if (!selected) return true;

  const url = String(officeUrl ?? "").trim();
  if (!url) return false;

  return officeUrlsMatch(url, selected);
};

/**
 * Country / `form_office` from `user.role` (not from site mapping).
 * Full-access roles keep every row; locked roles keep only matching `form_office`.
 */
export const matchesCountryAccess = (
  formOffice: string | null | undefined,
  allowedOffices: FormOfficeApi[],
  hasFullCountryAccess: boolean,
): boolean => {
  if (hasFullCountryAccess) return true;
  const normalized = normalizeFormOfficeForApi(formOffice);
  if (!normalized) return false;
  return allowedOffices.includes(normalized);
};

export interface AccessFilterOptions {
  /** `office_url` actually sent to the API. Empty / omitted → not a query. */
  officeUrlQuery?: string | null;
  /** `office` / `form_office` actually sent. `ALL` / empty → not a query. */
  officeQuery?: string | null;
  /**
   * Extra lock from `user.role` only (malta-* / ukraine-* / poland-*).
   * Do not pass site-derived offices here.
   */
  roleHasFullCountryAccess?: boolean;
  roleAllowedOffices?: FormOfficeApi[];
}

/**
 * Mirror backend list filters:
 * - 0 queries → no office/url filter
 * - 1 query → filter only by that field
 * - 2 queries → AND; if either field is missing or mismatches, drop the row
 */
export const filterRowsByAccess = <T,>(rows: T[], options: AccessFilterOptions): T[] => {
  const officeUrlQuery = toOfficeUrl(options.officeUrlQuery);
  const officeQuery = normalizeFormOfficeForApi(options.officeQuery);
  const applyRoleOfficeFilter = options.roleHasFullCountryAccess === false;

  return rows.filter((row) => {
    if (officeUrlQuery && !matchesSiteAccess(readRowOfficeUrl(row), officeUrlQuery)) return false;
    if (officeQuery) {
      const rowOffice = normalizeFormOfficeForApi(readRowFormOffice(row));
      if (!rowOffice || rowOffice !== officeQuery) return false;
    }
    if (
      applyRoleOfficeFilter &&
      !matchesCountryAccess(readRowFormOffice(row), options.roleAllowedOffices ?? [], false)
    ) {
      return false;
    }
    return true;
  });
};

/** Prefer backend total when the client did not drop extra rows. */
export const resolveAccessFilteredCount = (
  filteredLength: number,
  sourceLength: number,
  backendCount: number,
): number => (filteredLength === sourceLength ? backendCount : filteredLength);
